import { Config } from '@measured/puck';

import { AccordianBlock } from '../../shared/blocks/accordian/accordian';
import { CardBlock } from '../../shared/blocks/card/card';
import { ColumnBlock } from '../../shared/blocks/column/column';
import { FlexBlock } from '../../shared/blocks/flex/flex';
import { GridBlock } from '../../shared/blocks/grid/grid';
import { MarkdownBlock } from '../../shared/blocks/markdown/markdown';
import { SpacerBlock } from '../../shared/blocks/spacer/spacer';
import { TextBlock } from '../../shared/blocks/text/text';
import { ButtonBlock } from '../blocks/atoms/button/button';
import { createHeaderBlock } from '../blocks/atoms/header/header';
import { createAttachmentsBlock } from '../blocks/molecules/attachments/attachments';
import { createCalculationAppendixBlock } from '../blocks/molecules/calculation-appendix/calculation-appendix';
import { createCompositeQABlock } from '../blocks/molecules/composite-qa-block/create-composite-qa-block';
import { createDemographicsBarBlock } from '../blocks/molecules/demographics-bar/demographics-bar';
import { DisclaimerBlock } from '../blocks/molecules/disclaimer/disclaimer';
import { IndicatorsKeyBlock } from '../blocks/molecules/indicators/indicators-key';
import { createMeasureBreakdownBlock } from '../blocks/molecules/measure-breakdown/measure-breakdown-block';
import { createMeasuresBlock } from '../blocks/molecules/measures/measures';
import { createQuestionAnswerBlock } from '../blocks/molecules/questions-and-answers/questions-and-answers';
import { createReportTabsBlock } from '../blocks/molecules/report-tabs/report-tabs';
import { createShowCalculationBlock } from '../blocks/molecules/show-calculation/show-calculation';
import { createSubscaleBlock } from '../blocks/molecules/subscales/subscales';
import { SurveySet } from '../survey-types';

import { createRootConfig } from './create-root-config';

const categories = {
  layout: {
    components: ['ColumnBlock', 'FlexBlock', 'GridBlock', 'CardBlock', 'SpacerBlock'],
  },
  typography: {
    components: ['AccordianBlock', 'TextBlock', 'HeaderBlock', 'MarkdownBlock'],
  },
  info: {
    components: ['AttachmentsBlock', 'DemographicsBarBlock', 'DisclaimerBlock', 'IndicatorsKeyBlock'],
  },
  report: {
    components: [
      'ReportTabsBlock',
      'QuestionAnswerBlock',
      'CalculationAppendixBlock',
      'CompositeQABlock',
      'MeasuresBlock',
      'MeasureBreakdownBlock',
      'ShowCalculationBlock',
      'SubscaleBlock',
    ],
  },
};

const createConfig = ({
  processedSurveys,
  isPdf,
  allRespondentTypes,
  reportStatus,
  reportTimestamp,
}: {
  processedSurveys: SurveySet;
  isPdf?: boolean;
  allRespondentTypes: Set<string>;
  reportStatus?: string;
  reportTimestamp?: string;
}): Config => {
  return {
    root: createRootConfig(allRespondentTypes),
    categories: categories,
    components: {
      AccordianBlock,
      AttachmentsBlock: createAttachmentsBlock({ surveySet: processedSurveys }),
      ButtonBlock,
      CalculationAppendixBlock: createCalculationAppendixBlock({
        isPdf,
        surveySet: processedSurveys,
      }),
      CardBlock,
      ColumnBlock,
      CompositeQABlock: createCompositeQABlock({ surveySet: processedSurveys }),
      DemographicsBarBlock: createDemographicsBarBlock({ surveySet: processedSurveys }),
      DisclaimerBlock,
      FlexBlock,
      GridBlock,
      HeaderBlock: createHeaderBlock(),
      IndicatorsKeyBlock,
      MarkdownBlock,
      MeasureBreakdownBlock: createMeasureBreakdownBlock({ surveySet: processedSurveys }),
      MeasuresBlock: createMeasuresBlock({ surveySet: processedSurveys }),
      QuestionAnswerBlock: createQuestionAnswerBlock({ surveySet: processedSurveys }),
      ReportTabsBlock: createReportTabsBlock({ isPdf, reportStatus, reportTimestamp }),
      ShowCalculationBlock: createShowCalculationBlock({ surveySet: processedSurveys }),
      SpacerBlock,
      SubscaleBlock: createSubscaleBlock({ surveySet: processedSurveys }),
      TextBlock,
    },
  };
};

export { createConfig };
